import { createTransform } from 'redux-persist';
import { currentWeekStart } from '../utils/localDate';

/**
 * Storage engine + migration helpers for the large caches (mealPlan,
 * recipeIndex) that now persist to IndexedDB (web) / AsyncStorage (native)
 * instead of the ~5 MB synchronous localStorage bucket shared by the root
 * persist. Extracted from store.ts so the migration/transform logic is unit
 * testable without the store's side effects (localforage instance, timers).
 */

export interface AsyncStorageLike {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void> | Promise<any>;
  removeItem: (key: string) => Promise<void> | Promise<any>;
}

/**
 * Noop storage for SSR / environments without IndexedDB. redux-persist treats a
 * null getItem as "nothing persisted" and skips rehydration gracefully.
 */
export const createNoopStorage = (): AsyncStorageLike => ({
  getItem: () => Promise.resolve(null),
  setItem: () => Promise.resolve(),
  removeItem: () => Promise.resolve(),
});

/**
 * Reshape a slice stored in the legacy root-persist blob into the shape a
 * nested persist expects.
 *
 * Root persist stores each slice as a single serialized JSON string:
 *   persist:root = { mealPlan: '{"weeks":{...},"currentWeekStart":"..."}', ... }
 *
 * A nested persist (key='mealPlan') instead stores an object whose values are
 * each serialized per-field, because getStoredState runs JSON.parse twice:
 *   persist:mealPlan = { weeks: '{...}', currentWeekStart: '"..."', ... }
 *
 * Returns the nested-shaped serialized string, or null when there is nothing to
 * migrate.
 */
export function reshapeLegacySliceForNestedPersist(
  rootJson: string | null,
  sliceKey: string
): string | null {
  if (!rootJson) return null;
  const legacySliceStr = JSON.parse(rootJson)[sliceKey];
  if (legacySliceStr == null) return null;
  const sliceObj = JSON.parse(legacySliceStr);
  const nested: Record<string, string> = {};
  for (const field of Object.keys(sliceObj)) {
    nested[field] = JSON.stringify(sliceObj[field]);
  }
  return JSON.stringify(nested);
}

/**
 * Wrap a storage engine so the first read of a moved key falls back to the
 * legacy localStorage `persist:root` blob and write-throughs to the new engine.
 * The orphaned copies in persist:root are dropped automatically on the next
 * root persist, since these keys are removed from the root whitelist.
 *
 * `getLegacyRoot` is injected so the wrapper has no hard dependency on `window`
 * (keeps it testable in a node environment).
 */
export function makeMigratingStorage(
  base: AsyncStorageLike,
  getLegacyRoot: () => string | null
): AsyncStorageLike {
  return {
    setItem: (key, value) => base.setItem(key, value),
    removeItem: (key) => base.removeItem(key),
    getItem: async (key) => {
      const existing = await base.getItem(key);
      if (existing != null) return existing;
      try {
        const migrated = reshapeLegacySliceForNestedPersist(
          getLegacyRoot(),
          key.replace(/^persist:/, '')
        );
        if (migrated == null) return null;
        await base.setItem(key, migrated);
        return migrated;
      } catch (error) {
        console.warn(`Failed to migrate ${key} from legacy localStorage:`, error);
        return null;
      }
    },
  };
}

/**
 * Reset currentWeekStart to the current week on rehydrate. mealPlan is a nested
 * persist, so transforms run per slice FIELD — this targets `currentWeekStart`.
 */
export const mealPlanTransform = createTransform(
  (inboundState: any) => inboundState,
  () => currentWeekStart(),
  { whitelist: ['currentWeekStart'] }
);

/**
 * Guard against unbounded growth: truncate the recipe index to 2000 entries
 * before persisting. recipeIndex is a nested persist, so this targets the
 * `entries` field directly (the field value is the entries array).
 */
export const recipeIndexTransform = createTransform(
  (inbound: any) =>
    Array.isArray(inbound) && inbound.length > 2000 ? inbound.slice(0, 2000) : inbound,
  (outbound: any) => outbound,
  { whitelist: ['entries'] }
);
